import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { CaretDown, CaretUp } from '@phosphor-icons/react';
import EmptyState from './EmptyState';

const SKELETON_ROWS = ['s1', 's2', 's3', 's4', 's5', 's6'];

function Table({
  isLoading,
  sortKey,
  onSort,
  format,
  sort,
  data,
  zebra,
  emptySpiel,
}) {
  const [sortBy, orderBy] = useMemo(() => (sort || '').split(':'), [sort]);

  const handleSort = (col) => (e) => {
    e.preventDefault();
    const switchOrder = orderBy === 'asc' ? 'desc' : 'asc';
    onSort((state) => ({
      ...state,
      [sortKey]: `${col.sortKey}:${
        sortBy !== col.sortKey ? 'asc' : switchOrder
      }`,
    }));
  };
  return (
    // Wrap this component in a div element and add 'flex flex-col' className
    // Also add fixed height for horizontal scroll
    <div
      className={cn(
        'overflow-auto flex-grow',
        !isLoading && data?.length < 1
          ? 'flex items-center justify-center'
          : '',
      )}
    >
      {isLoading || data?.length < 1 ? (
        <div className={cn(isLoading ? 'h-auto' : 'h-full')}>
          {isLoading ? (
            <div className="p-4 space-y-2">
              {SKELETON_ROWS.map((k) => (
                <div
                  key={k}
                  className="animate-pulse bg-neutral-100 h-row w-full rounded"
                />
              ))}
            </div>
          ) : (
            <div className="h-full">
              <EmptyState variant="detail" statement={emptySpiel} />
            </div>
          )}
        </div>
      ) : (
        <table className="h-auto w-full table-auto relative">
          <thead className="sticky top-0 z-10 bg-neutral-100 h-thead border-b border-neutral-300">
            <tr>
              {React.Children.toArray(
                format.map((col) => (
                  <th
                    key={col?.id || col?.key}
                    scope="col"
                    width={col.width || 'initial'}
                  >
                    {onSort && col.sortKey ? (
                      <a
                        href="/"
                        onClick={handleSort(col)}
                        className={cn(
                          'whitespace-nowrap h-thead px-4 text-left text-xs font-semibold uppercase tracking-[.05em] flex items-center gap-1.5 transition-colors hover:text-neutral-900',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600',
                          sortBy === col.sortKey
                            ? 'text-brand-700'
                            : 'text-neutral-500',
                        )}
                      >
                        <span>{col.label}</span>
                        {sortBy === col.sortKey &&
                          (orderBy === 'desc' ? (
                            <CaretUp size={12} weight="bold" />
                          ) : (
                            <CaretDown size={12} weight="bold" />
                          ))}
                      </a>
                    ) : (
                      <span
                        className={cn(
                          'h-thead px-4 text-left text-neutral-500 text-xs font-semibold tracking-[.05em] uppercase whitespace-nowrap flex items-center flex-shrink-0',
                          col.labelClassName || '',
                        )}
                      >
                        {col.label}
                      </span>
                    )}
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {React.Children.toArray(
              data.map((row, idx) => (
                <tr
                  key={JSON.stringify(row)}
                  className={cn(
                    'group h-row border-b border-neutral-100 transition-colors',
                    'hover:bg-neutral-100',
                    zebra && idx % 2 === 1 ? 'bg-neutral-50' : 'bg-white',
                  )}
                >
                  {React.Children.toArray(
                    format.map((col) => {
                      const content = (() => {
                        if (typeof col.key === 'function') return col.key(row);
                        return row[col.key];
                      })();
                      return (
                        <td
                          className={cn(
                            // tabular on every cell: guarantees IDs, counts and
                            // dates line up down a column without per-column
                            // bookkeeping, and costs nothing on text cells.
                            'px-4 text-left text-sm font-medium text-neutral-900 tabular-nums group-hover:font-semibold',
                            col.contentClassName || '',
                          )}
                          width={col.width || 'initial'}
                        >
                          {content}
                        </td>
                      );
                    }),
                  )}
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

Table.defaultProps = {
  isLoading: false,
  sortKey: 'sort',
  onSort: false,
  data: [],
  sort: '',
  zebra: false,
  emptySpiel: 'There are no items.',
};

Table.propTypes = {
  onSort: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  format: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.instanceOf(Array),
  isLoading: PropTypes.bool,
  sortKey: PropTypes.string,
  sort: PropTypes.string,
  zebra: PropTypes.bool,
  emptySpiel: PropTypes.string,
};

export default Table;
